/**
 * Sync trigger registration.
 * Fires sync on: visibility change, online event, periodic interval.
 * No Background Sync API dependency (iOS incompatible).
 */

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const STARTUP_DELAY_MS = 3000;

/**
 * Register automatic sync triggers. Returns a cleanup function.
 * @param syncFn - The function to call when a sync should occur.
 * @param intervalMs - Periodic sync interval (default 5 minutes).
 */
export function initSyncTriggers(
  syncFn: () => void,
  intervalMs: number = DEFAULT_INTERVAL_MS,
): () => void {
  const cleanupFns: Array<() => void> = [];

  // Sync on app foreground
  const handleVisibility = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      syncFn();
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);
  cleanupFns.push(() => document.removeEventListener('visibilitychange', handleVisibility));

  // Sync on network reconnect
  const handleOnline = () => { syncFn(); };
  window.addEventListener('online', handleOnline);
  cleanupFns.push(() => window.removeEventListener('online', handleOnline));

  // Periodic sync
  const intervalId = setInterval(() => {
    if (navigator.onLine) syncFn();
  }, intervalMs);
  cleanupFns.push(() => clearInterval(intervalId));

  // Initial sync after startup delay
  const timeoutId = setTimeout(() => {
    if (navigator.onLine) syncFn();
  }, STARTUP_DELAY_MS);
  cleanupFns.push(() => clearTimeout(timeoutId));

  return () => { cleanupFns.forEach((fn) => fn()); };
}
